/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { useRequest } from 'ahooks';
import { passport } from '@adp-studio/api-schema';
import {
  setUserInfo,
  useLoginStatus,
  resetUserStore,
  type UserInfo,
} from '@adp-foundation/account-adapter';

import {
  isFrontendLoginRedirect,
  navigateFrontendLoginRedirect,
  resolveLoginRedirect,
} from './redirect-url';
import {
  FALLBACK_AUTH_METHODS,
  getLoginEndpoint,
  parseAuthMethods,
  type AuthMethods,
  type LoginMethod,
} from './auth-methods';

export const useAuthMethodsService = () =>
  useRequest<AuthMethods>(async () => {
    try {
      const res = await fetch('/api/auth/methods');
      if (res.ok) {
        return parseAuthMethods(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch auth methods', e);
    }
    return FALLBACK_AUTH_METHODS;
  });

interface LDAPLoginResponse {
  code?: number;
  data?: UserInfo;
  detail?: string;
  msg?: string;
}

async function loginWithLDAP(
  identifier: string,
  password: string,
): Promise<UserInfo> {
  const response = await fetch(getLoginEndpoint('ldap'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ identifier, password }),
  });
  const payload = (await response
    .json()
    .catch(() => null)) as LDAPLoginResponse | null;

  if (!response.ok || payload?.code !== 0 || !payload.data) {
    throw new Error(payload?.msg || payload?.detail || 'LDAP login failed');
  }

  return payload.data;
}

export const useLoginService = ({
  identifier,
  password,
  loginMethod,
}: {
  identifier: string;
  password: string;
  loginMethod: LoginMethod;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);

  const finishLogin = (data: UserInfo) => {
    setUserInfo(data);
    const redirectUrl = resolveLoginRedirect(searchParams.get('redirect'));
    if (isFrontendLoginRedirect(redirectUrl)) {
      navigateFrontendLoginRedirect(redirectUrl);
    } else {
      navigate(redirectUrl);
    }
  };

  const loginService = useRequest(
    async () => {
      if (loginMethod === 'ldap') {
        return loginWithLDAP(identifier, password);
      }

      const res = (await passport.PassportWebEmailLoginPost({
        email: identifier,
        password,
      })) as unknown as { data: UserInfo };
      return res.data;
    },
    {
      manual: true,
      onSuccess: data => {
        if (data.must_change_password || data.password_expired) {
          setPasswordChangeRequired(true);
          return;
        }
        finishLogin(data);
      },
    },
  );

  const registerService = useRequest(
    async () => {
      const res = (await passport.PassportWebEmailRegisterV2Post({
        email: identifier,
        password,
      })) as unknown as { data: UserInfo };
      return res.data;
    },
    {
      manual: true,
      onSuccess: finishLogin,
    },
  );

  const passwordChangeService = useRequest(
    async (newPassword: string) => {
      await passport.UserChangePassword({
        old_password: password,
        new_password: newPassword,
      });
      const res = (await passport.PassportWebEmailLoginPost({
        email: identifier,
        password: newPassword,
      })) as unknown as { data: UserInfo };
      return res.data;
    },
    {
      manual: true,
      onSuccess: data => {
        setPasswordChangeRequired(false);
        finishLogin(data);
      },
    },
  );

  const loginStatus = useLoginStatus();

  useEffect(() => {
    if (searchParams.get('error') === 'session_expired') {
      localStorage.removeItem('coze_current_uid');
      resetUserStore();
      return;
    }
    if (loginStatus === 'logined') {
      const redirectUrl = resolveLoginRedirect(searchParams.get('redirect'));
      if (isFrontendLoginRedirect(redirectUrl)) {
        navigateFrontendLoginRedirect(redirectUrl);
      } else {
        navigate(redirectUrl);
      }
    }
  }, [loginStatus, searchParams, navigate]);

  return {
    login: loginService.run,
    register: registerService.run,
    loginLoading: loginService.loading,
    registerLoading: registerService.loading,
    loginError: loginService.error?.message || '',
    passwordChangeRequired,
    changePassword: passwordChangeService.run,
    passwordChangeLoading: passwordChangeService.loading,
    passwordChangeError: passwordChangeService.error?.message || '',
  };
};
